import { dateToString } from '@/helpers/dateTime';
import riderService from '../../services/rider';

// In-flight route-details requests by `${routeId}_${date}`. routeDetailsMap only
// holds finished responses, so rides sharing a key (e.g. the outbound and return
// leg of the same day) and overlapping refreshes reuse one pending request.
const routeDetailsRequests = new Map();

const state = {
  nextRides: [],
  selectedRide: null,
  routeDetails: null,
  routeDetailsMap: {},
  vehicleLocation: null,
  destinations: [],
  loading: {
    rides: false,
    routeDetails: false,
    vehicleLocation: false,
  },
  error: null,
};

const getters = {
  hasMultipleRides: (s) => s.nextRides && s.nextRides.length > 1,
  isActiveRide: (s) => s.selectedRide?.workgroupStatus === 'ACTIVE',

  // Returns all rides that share the same templateId as the given ride (including the ride itself)
  getRecurringRides: (s) => (ride) => {
    if (!ride) return [];
    if (!ride.templateId) return [ride];
    return s.nextRides.filter((r) => r.templateId === ride.templateId);
  },

  // Central ride status getter - returns status string for any ride
  getRideStatus: () => (ride) => {
    if (!ride) return null;
    if (ride.activeRide) return 'active';
    if (ride.isWaitlisted) return 'waitlisted';
    if (!ride.routeId || ride.workgroupStatus !== 'ACTIVE') return 'requested';
    if (ride.reserved) return 'reserved';
    if (ride.notUsing) return 'notUsing';
    return 'regular';
  },
};

const mutations = {
  /* eslint-disable no-param-reassign */
  SET_NEXT_RIDES(s, rides) {
    s.nextRides = rides || [];
  },
  SET_ROUTE_DETAILS(s, details) {
    s.routeDetails = details;
  },
  SET_VEHICLE_LOCATION(s, location) {
    s.vehicleLocation = location;
  },
  SET_DESTINATIONS(s, destinations) {
    s.destinations = destinations;
  },
  SET_LOADING(s, { key, value }) {
    s.loading[key] = value;
  },
  SET_ERROR(s, error) {
    s.error = error;
  },
  SET_SELECTED_RIDE(s, ride) {
    s.selectedRide = ride;
  },
  SET_ROUTE_DETAILS_MAP(s, { key, details }) {
    s.routeDetailsMap[key] = details;
  },
  CLEAR_STATE(s) {
    s.nextRides = [];
    s.selectedRide = null;
    s.routeDetails = null;
    s.routeDetailsMap = {};
    s.vehicleLocation = null;
    s.error = null;
  },
};

const actions = {
  async fetchMyNextRides({ commit }, { skipCommit = false } = {}) {
    commit('SET_LOADING', { key: 'rides', value: true });
    commit('SET_ERROR', null);

    try {
      const response = await riderService.getMyNextRides();
      let sortedRides = response || [];

      // Filter and sort rides by firstDepartureDate ascending
      sortedRides = sortedRides
        .filter((ride) => ride.firstDepartureDate)
        .filter((ride) => ride.workgroupType === 'SHUTTLE')
        .sort((a, b) => a.firstDepartureDate - b.firstDepartureDate);

      // Only commit if not called from refreshMyTrips to prevent race condition
      if (!skipCommit) {
        commit('SET_NEXT_RIDES', sortedRides);
      }

      return sortedRides;
    } catch (error) {
      commit('SET_ERROR', error.response?.data?.error?.message || error.message);
      throw error;
    } finally {
      commit('SET_LOADING', { key: 'rides', value: false });
    }
  },

  async fetchDestinations({ commit, state: s }) {
    // Return cached destinations if already loaded
    if (s.destinations && s.destinations.length > 0) {
      return s.destinations;
    }

    try {
      const { response } = await riderService.getDestinations();
      const destinations = response || [];
      commit('SET_DESTINATIONS', destinations);
      return destinations;
    } catch (error) {
      console.error('Failed to fetch destinations:', error);
      return [];
    }
  },

  async fetchRouteDetails({ commit }, { routeId, reservationDay }) {
    commit('SET_LOADING', { key: 'routeDetails', value: true });

    try {
      const response = await riderService.getRouteDetails(routeId, reservationDay);
      commit('SET_ROUTE_DETAILS', response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch route details:', error);
      throw error;
    } finally {
      commit('SET_LOADING', { key: 'routeDetails', value: false });
    }
  },

  async fetchAllRouteDetails({ commit, state: s }) {
    commit('SET_LOADING', { key: 'routeDetails', value: true });
    try {
      const fetchPromises = new Set();

      s.nextRides.forEach((ride) => {
        if (!ride.routeId || !ride.firstDepartureDate) return;

        // Format date to YYYY-MM-DD (timezone-insensitive)
        const dateStr = dateToString(ride.firstDepartureDate);

        // Create cache key: routeId + date
        const cacheKey = `${ride.routeId}_${dateStr}`;

        // Check if this combination already exists in cache
        if (s.routeDetailsMap[cacheKey]) {
          return; // Skip if already fetched
        }

        // Fetch route details unless a request for this key is already pending
        if (!routeDetailsRequests.has(cacheKey)) {
          const promise = riderService
            .getRouteDetails(ride.routeId, dateStr)
            .then((response) => {
              // Store in map with date included in the object
              commit('SET_ROUTE_DETAILS_MAP', {
                key: cacheKey,
                details: {
                  ...response,
                  date: dateStr,
                },
              });
            })
            .catch((error) => {
              console.error(`Failed to fetch route details for ${cacheKey}:`, error);
            })
            .finally(() => {
              routeDetailsRequests.delete(cacheKey);
            });

          routeDetailsRequests.set(cacheKey, promise);
        }

        fetchPromises.add(routeDetailsRequests.get(cacheKey));
      });

      await Promise.all(fetchPromises);
    } finally {
      commit('SET_LOADING', { key: 'routeDetails', value: false });
    }
  },

  async fetchVehicleLocation({ commit }, workgroupInstanceId) {
    commit('SET_LOADING', { key: 'vehicleLocation', value: true });

    try {
      const { response } = await riderService.getVehicleLocation(workgroupInstanceId);

      // Transform vehicle location to map marker format
      const vehicleLocation = response
        ? {
            lat: parseFloat(response.LATITUDE),
            lng: parseFloat(response.LONGITUDE),
            ...response,
          }
        : null;

      commit('SET_VEHICLE_LOCATION', vehicleLocation);
      return vehicleLocation;
    } catch (error) {
      console.error('Failed to fetch vehicle location:', error);
      return null;
    } finally {
      commit('SET_LOADING', { key: 'vehicleLocation', value: false });
    }
  },

  async fetchActiveRideStatus({ commit, state: s }, { routeInstanceId, stationId }) {
    try {
      const response = await riderService.getActiveRideStatus(routeInstanceId, stationId);
      const updates = response.data;

      // Update selected ride with live data
      if (updates && s.selectedRide) {
        commit('SET_SELECTED_RIDE', {
          ...s.selectedRide,
          delay: updates.delay,
          eta: updates.eta,
          actualArrival: updates.actualArrival,
          activeRide: updates.activeRide,
          routeInstanceState: updates.routeInstanceState,
        });
      }

      return updates;
    } catch (error) {
      console.error('Failed to fetch active ride status:', error);
      return null;
    }
  },

  async createOrUpdateReservation({ dispatch }, reservationData) {
    try {
      await riderService.createOrUpdateReservation(reservationData);
      // Refresh all trip data after the reservation is updated
      await dispatch('refreshMyTrips');
      return { success: true };
    } catch (error) {
      console.error('Failed to update reservation:', error);
      throw error;
    }
  },

  async cancelAllReservations({ dispatch }, reservationData) {
    try {
      await riderService.cancelAllReservations(reservationData);
      // Refresh all trip data after cancellation
      await dispatch('refreshMyTrips');
      return { success: true };
    } catch (error) {
      console.error('Failed to cancel all reservations:', error);
      throw error;
    }
  },

  async cancelDemand({ dispatch }, demandData) {
    try {
      await riderService.cancelDemand(demandData);
      // Refresh all trip data after cancellation
      await dispatch('refreshMyTrips');
      return { success: true };
    } catch (error) {
      console.error('Failed to cancel demand:', error);
      throw error;
    }
  },

  async unsubscribe({ dispatch }, routeId) {
    try {
      await riderService.unsubscribe(routeId);
      // Refresh all trip data after unsubscribing
      await dispatch('refreshMyTrips');
      return { success: true };
    } catch (error) {
      console.error('Failed to unsubscribe:', error);
      throw error;
    }
  },

  async optOut({ dispatch }, workgroupInstanceId) {
    try {
      await riderService.optOut(workgroupInstanceId);
      // Refresh rides after opt-out
      await dispatch('refreshMyTrips');
      return { success: true };
    } catch (error) {
      console.error('Failed to opt out:', error);
      throw error;
    }
  },

  async refreshMyTrips({ dispatch, commit, state: s, rootGetters, rootState }) {
    try {
      // 1. Fetch destinations (cached)
      await dispatch('fetchDestinations');

      // 2. Fetch next rides
      const rides = await dispatch('fetchMyNextRides', { skipCommit: false });

      // 3. Fetch route details for all rides
      if (rides && rides.length > 0) {
        await dispatch('fetchAllRouteDetails');

        // 4. Map details, destination, and origin info to each ride
        const updatedRides = rides.map((ride) => {
          const dateStr = dateToString(ride.firstDepartureDate);
          const cacheKey = `${ride.routeId}_${dateStr}`;
          const details = s.routeDetailsMap[cacheKey] || null;
          const destination = s.destinations.find((dest) => dest.id === ride.destinationId) || null;

          // Determine ride status using getter
          // const rideStatus = getters.getRideStatus()(ride);

          // For requested rides, determine origin (home address or campus)
          let requestOrigin = null;

          const isToCampus = ride.toType === 'CAMPUS';

          if (isToCampus) {
            // Ride TO campus - origin is home address
            if (ride.fromType === 'PERSONNEL_HOME_ADDRESS' && rootState.userDetails?.personnel?.homeLocation) {
              requestOrigin = {
                type: 'home',
                location: rootState.userDetails.personnel.homeLocation,
              };
            }
          } else {
            // Ride FROM campus - origin is campus
            const campusId = ride.fromTerminalReferenceId;
            if (campusId && rootGetters.campuses) {
              const campus = rootGetters.campuses(campusId);
              if (campus) {
                requestOrigin = {
                  type: 'campus',
                  location: campus.location,
                  campus,
                };
              }
            }
          }

          return {
            ...ride,
            details,
            destination,
            requestOrigin,
          };
        });

        // 5. Update store with enriched rides (single commit with all data)
        commit('SET_NEXT_RIDES', updatedRides);

        // 6. Auto-select default ride if none selected
        if (!s.selectedRide && updatedRides.length > 0) {
          // Prioritize first ACTIVE ride, otherwise select first ride
          const activeRide = updatedRides.find((ride) => ride.workgroupStatus === 'ACTIVE');
          const defaultRide = activeRide || updatedRides[0];
          commit('SET_SELECTED_RIDE', defaultRide);
        }
      }

      return { success: true };
    } catch (error) {
      console.error('Failed to refresh trips:', error);
      throw error;
    }
  },

  selectRide({ commit, state: s }, ride) {
    // Only update if ride actually changed (compare by properties, not object reference)
    if (!ride) return;

    // Compare rides by their unique properties (same logic as tripList)
    if (s.selectedRide?.firstDepartureDate === ride.firstDepartureDate && s.selectedRide?.routeId === ride.routeId) {
      return;
    }

    commit('SET_SELECTED_RIDE', ride);
  },

  setSelectedRide({ commit }, ride) {
    commit('SET_SELECTED_RIDE', ride);
  },
  clearMyTrips({ commit }) {
    commit('CLEAR_STATE');
  },
  clearVehicleLocation({ commit }) {
    commit('SET_VEHICLE_LOCATION', null);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
